import { createContext, useEffect, useState, type ReactNode } from "react";
import { useRouter } from "next/router";
import hash from "object-hash";

// Interface for initial parameters to initialize the save bar
interface IInitSaveBarParams {
  shouldWarnIfRouteChange?: boolean;
  renderSaveBar: boolean;
}

// Context properties for the SaveBar
interface SaveBarContextProps {
  enableSaveBtn: boolean;
  isGracePeriod: boolean;
  isSaveActive: boolean;
  initSaveBarParams: {
    shouldWarnIfRouteChange?: boolean;
    renderSaveBar: boolean;
  };
  initSaveBar: (params: IInitSaveBarParams) => void;
  compareStates: <T>(initialState: T, state: T) => boolean;
  handleSaveClick: () => void;
}

// Create the SaveBar context with default values
const SaveBarContext = createContext<SaveBarContextProps>({
  enableSaveBtn: false,
  isGracePeriod: true,
  isSaveActive: false,
  initSaveBarParams: {
    renderSaveBar: false,
    shouldWarnIfRouteChange: false,
  },
  initSaveBar: () => {},
  compareStates: () => false,
  handleSaveClick: () => {},
});

// Props for the SaveBarProvider component
interface SaveBarProviderProps {
  children: ReactNode;
}

// Provider component for the SaveBar context
const SaveBarProvider: React.FC<SaveBarProviderProps> = ({ children }) => {
  // Function to update a single value in the save bar state
  const onChangeValue = (key, value) => {
    toggleSaveBar((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  // Function to update multiple values in the save bar state
  const onChangeMultipleValues = (values) => {
    toggleSaveBar((prevState) => ({
      ...prevState,
      ...values,
    }));
  };

  // Function to compare two states and update the enableSaveBtn flag
  const compareStates = <T,>(initialState: T, state: T): boolean => {
    const hasChanged = hash(initialState) !== hash(state);
    onChangeValue("enableSaveBtn", hasChanged);
    return hasChanged;
  };

  // Function to initialize the save bar with given parameters
  const initSaveBar = (params: IInitSaveBarParams) => {
    setTimeout(() => {
      onChangeValue("initSaveBarParams", {
        renderSaveBar: params.renderSaveBar,
        shouldWarnIfRouteChange: params.shouldWarnIfRouteChange,
      });
    }, 100);
  };

  // Initial state for the save bar context
  const saveBarState: SaveBarContextProps = {
    enableSaveBtn: false,
    isGracePeriod: true,
    isSaveActive: false,
    initSaveBarParams: {
      renderSaveBar: false,
      shouldWarnIfRouteChange: false,
    },
    initSaveBar,
    compareStates,
    handleSaveClick: () => {
      onChangeValue("isSaveActive", true);
    },
  };

  // State to manage the save bar context
  const [saveBar, toggleSaveBar] = useState(saveBarState);
  const router = useRouter();

  // Effect to manage the save active state and reset it after a timeout
  // biome-ignore lint/correctness/useExhaustiveDependencies: <Don't need more dependencies>
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (saveBar.isSaveActive) {
      timer = setTimeout(() => {
        onChangeValue("isSaveActive", false);
      }, 1000);
    }
    return () => clearTimeout(timer); // Cleanup timer on unmount
  }, [saveBar.isSaveActive]);

  // Effect to manage the grace period and reset it after a timeout
  // biome-ignore lint/correctness/useExhaustiveDependencies: <Only need the router path>
  useEffect(() => {
    onChangeMultipleValues({
      initSaveBarParams: {
        shouldWarnIfRouteChange: false,
        renderSaveBar: false,
      },
      isGracePeriod: true,
    });
    const timer = setTimeout(() => {
      onChangeValue("isGracePeriod", false);
    }, 3000);
    return () => clearTimeout(timer); // Cleanup timer on unmount
  }, [router.asPath]);

  // Effect to add and remove the click event listener for turning off the grace period
  // biome-ignore lint/correctness/useExhaustiveDependencies: <Don't need to re-run this effect>
  useEffect(() => {
    document.addEventListener("click", () =>
      onChangeValue("isGracePeriod", false),
    );
    return () =>
      document.removeEventListener("click", () =>
        onChangeValue("isGracePeriod", false),
      ); // Cleanup event listener on unmount
  }, []);

  // Provide the save bar context to children components
  return (
    <SaveBarContext.Provider value={saveBar}>
      {children}
    </SaveBarContext.Provider>
  );
};

export { SaveBarProvider, SaveBarContext };
